import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setProfile } from '../store/userSlice';
import googleIcon from '../assets/images/google.svg';
import facebookIcon from '../assets/images/facebook.svg';
import {
  signUp as signUpUser,
  signInWithGoogleSupabase,
} from '../services/auth';
import AuthPrimaryButton from './AuthPrimaryButton';
import AuthFormContainer from './AuthFormContainer';

export default function SignUpCard() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordLengthError, setPasswordLengthError] = useState('');
  const [serverErrors, setServerErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validationSchema = Yup.object({
    fullName: Yup.string().trim().required('Full name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords do not match')
      .required('Confirm Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: () => {},
    validateOnBlur: false,
    validateOnChange: false,
  });

  const checkPasswordMatch = (pass, confirmPass) => {
    if (confirmPass && pass && pass !== confirmPass) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
      // Clear Formik errors when passwords match
      if (pass && confirmPass && pass === confirmPass) {
        formik.setFieldError('password', undefined);
        formik.setFieldError('confirmPassword', undefined);
      }
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    formik.setFieldValue('password', newPassword);

    // Clear server errors when user starts typing
    if (serverErrors.password) {
      setServerErrors((prev) => ({ ...prev, password: '' }));
    }

    if (newPassword && newPassword.length < 8) {
      setPasswordLengthError('Password must be at least 8 characters long');
    } else {
      setPasswordLengthError('');
      // Clear Formik error when password length is valid
      if (newPassword.length >= 8) {
        formik.setFieldError('password', undefined);
      }
    }

    checkPasswordMatch(newPassword, confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    checkPasswordMatch(password, newConfirmPassword);
    formik.setFieldValue('confirmPassword', newConfirmPassword);
    
    // Clear server errors when user starts typing
    if (serverErrors.confirmPassword) {
      setServerErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
    
    // Clear Formik error when confirm password changes
    if (password && newConfirmPassword && password === newConfirmPassword) {
      formik.setFieldError('confirmPassword', undefined);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setServerErrors({});

    formik.setValues({
      fullName,
      email,
      password,
      confirmPassword,
      // gender, year, month, day – moved to another form later
    });
    formik.setTouched({
      fullName: true,
      email: true,
      password: true,
      confirmPassword: true,
      // gender, year, month, day – moved to another form later
    });
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    if (!fullName || !email || !password || !confirmPassword) {
      alert('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    if (password.length < 8) {
      alert('Password must be at least 8 characters long');
      setIsSubmitting(false);
      return;
    }

    if (passwordLengthError) {
      alert('Please fix the password length before submitting.');
      setIsSubmitting(false);
      return;
    }

    if (passwordError) {
      alert('Please fix the password mismatch before submitting.');
      setIsSubmitting(false);
      return;
    }

    if (password !== confirmPassword) {
      alert('Passwords do not match. Please try again.');
      setIsSubmitting(false);
      return;
    }

    // Gender & Date of birth – moved to another form later
    // const isValidDOB = (() => { ... })();
    // if (year || month || day) { ... }
    // const dateOfBirthIso = ...

    try {
      const { user } = await signUpUser({
        fullName,
        email,
        password,
        confirmPassword,
        // gender and dateOfBirth – not sent; moved to another form later
      });

      dispatch(
        setProfile({
          fullName: user?.full_name || fullName,
          email: user?.email || email,
          // gender, dateOfBirth – moved to another form later
        })
      );
      const finalFullName = user?.full_name || fullName;
      const finalEmail = user?.email || email;
      localStorage.setItem('userFullName', finalFullName);
      localStorage.setItem('userEmail', finalEmail);
      // userGender, userDOB – moved to another form later
      navigate('/otp-verification', { state: { email: finalEmail } });
    } catch (err) {
      // Error handling is done via toast notifications in signUpUser

      const serverData = err?.response?.data;
      const rawErrors = serverData?.errors;

      if (rawErrors && typeof rawErrors === 'object') {
        const mapped = {};
        const setIf = (key, value) => {
          if (value && value.length)
            mapped[key] = Array.isArray(value)
              ? value.join(' ')
              : String(value);
        };

        setIf('fullName', rawErrors.full_name);
        setIf('email', rawErrors.email);
        setIf('password', rawErrors.password);
        setIf('confirmPassword', rawErrors.confirm_password);
        // setIf('gender', rawErrors.gender);
        // setIf('date_of_birth', rawErrors.date_of_birth);

        if (mapped.fullName) formik.setFieldError('fullName', mapped.fullName);
        if (mapped.email) formik.setFieldError('email', mapped.email);
        if (mapped.password) formik.setFieldError('password', mapped.password);
        if (mapped.confirmPassword)
          formik.setFieldError('confirmPassword', mapped.confirmPassword);
        // if (mapped.gender) formik.setFieldError('gender', mapped.gender);
        // if (mapped.date_of_birth) { formik.setFieldError('day'/'month'/'year', ...); }

        setServerErrors(mapped);
        setIsSubmitting(false);
        return;
      }

      const generic =
        serverData?.detail ||
        serverData?.message ||
        err?.message ||
        'Registration failed';
      setServerErrors({ email: generic });
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthFormContainer>
      <div className="flex flex-col items-center gap-1 md:gap-2 w-full">
        <p className="text-[#2B3133] font-libreBaskerville text-2xl md:text-3xl leading-[1em] text-center">
          Create an Account
        </p>
        <p className="text-[#2B3133] font-poppins text-sm md:text-base font-normal text-center">
          Already have an account?{' '}
          <a className="underline text-[#119975]" href="/signin">
            Sign In
          </a>
        </p>
      </div>
      <form onSubmit={handleSignUp} className="flex flex-col gap-4 mt-6">
        <div className="flex flex-col items-center gap-4 w-full">
          <div className="flex flex-col justify-center items-start gap-2 w-full min-h-[72px] relative">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              Full Name
            </label>
            <input
              type="text"
              placeholder="Iryna Masechko"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                formik.setFieldValue('fullName', e.target.value);
              }}
              className="mt-2 w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins"
              required
            />
            {formik.touched.fullName && formik.errors.fullName && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {formik.errors.fullName}
              </p>
            )}
            {!formik.errors.fullName && serverErrors.fullName && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {serverErrors.fullName}
              </p>
            )}
          </div>

          <div className="flex flex-col justify-center items-start gap-2 w-full min-h-[72px] relative">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              E-mail
            </label>
            <input
              type="email"
              placeholder="example@gmail.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                formik.setFieldValue('email', e.target.value);
              }}
              className="mt-2 w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins"
              required
            />
            {formik.touched.email && formik.errors.email && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {formik.errors.email}
              </p>
            )}
            {!formik.errors.email && serverErrors.email && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {serverErrors.email}
              </p>
            )}
          </div>

          <div className="flex flex-col justify-center items-start gap-2 w-full min-h-[72px] relative">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              Password
            </label>
            <div className="mt-2 flex items-center w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="@#*%"
                value={password}
                onChange={handlePasswordChange}
                className="flex-1 bg-transparent outline-none px-5 font-poppins"
                required
              />
              <div className="w-px h-10 bg-[#CFD9E0]" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="pl-3 pr-4 cursor-pointer hover:opacity-70 transition-opacity"
              >
                {showPassword ? (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z"
                      fill="#889696"
                    />
                    <path
                      d="M2 2L22 22"
                      stroke="#889696"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z"
                      fill="#889696"
                    />
                  </svg>
                )}
              </button>
            </div>
            {passwordLengthError && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10">
                {passwordLengthError}
              </p>
            )}
            {formik.touched.password && formik.errors.password && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {formik.errors.password}
              </p>
            )}
            {!formik.errors.password && serverErrors.password && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {serverErrors.password}
              </p>
            )}
          </div>

          <div className={`flex flex-col justify-center items-start gap-2 w-full min-h-[72px] relative ${
            (passwordLengthError || formik.touched.password && formik.errors.password || serverErrors.password) ? 'mt-6' : ''
          }`}>
            <label className="text-[#889696] font-poppins text-sm font-medium">
              Confirm Password
            </label>
            <div className="mt-2 flex items-center w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="@#*%"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                className={`flex-1 bg-transparent outline-none px-5 font-poppins ${passwordError ? 'border-red-500' : ''}`}
                required
              />
              <div className="w-px h-10 bg-[#CFD9E0]" />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="pl-3 pr-4 cursor-pointer hover:opacity-70 transition-opacity"
              >
                {showConfirmPassword ? (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z"
                      fill="#889696"
                    />
                    <path
                      d="M2 2L22 22"
                      stroke="#889696"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z"
                      fill="#889696"
                    />
                  </svg>
                )}
              </button>
            </div>
            {passwordError && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10">
                {passwordError}
              </p>
            )}
            {formik.touched.confirmPassword &&
              formik.errors.confirmPassword && (
                <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                  {formik.errors.confirmPassword}
                </p>
              )}
            {!formik.errors.confirmPassword && serverErrors.confirmPassword && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {serverErrors.confirmPassword}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-2">
          <label className="flex items-center gap-2 text-[#889696] font-poppins text-sm">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-[#CFD9E0]"
            />
            Remember me
          </label>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-[#2B3133] font-poppins text-sm font-medium underline hover:text-[#119975] transition-colors"
          >
            Forgot Password?
          </button>
        </div>

        <AuthPrimaryButton
          type="submit"
          disabled={isSubmitting}
          className="mt-3"
        >
          {isSubmitting ? 'Signing Up...' : 'Sign Up'}
        </AuthPrimaryButton>
      </form>

      <div className="my-5 md:my-6">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="h-px bg-[#CBD5E0] flex-1" />
          <span className="text-[#889696] font-poppins text-xs font-medium">
            OR
          </span>
          <div className="h-px bg-[#CBD5E0] flex-1" />
        </div>
      </div>

      <div className="space-y-2 md:space-y-3">
        <button
          onClick={async () => {
            try {
              await signInWithGoogleSupabase();
            } catch (e) {
              // no-op: sign-in errors are surfaced by provider/callback route
            }
          }}
          className="w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-white flex items-center gap-3 text-[#889696] font-poppins px-5"
        >
          <img src={googleIcon} alt="Google" className="w-5 h-5 md:w-6 md:h-6" />
          <span className="text-[#889696] font-poppins text-base md:text-lg font-medium flex-1 text-center">
            Continue with Google
          </span>
        </button>
        <button className="w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-white flex items-center gap-3 text-[#889696] font-poppins px-5">
          <img src={facebookIcon} alt="Facebook" className="w-5 h-5 md:w-6 md:h-6" />
          <span className="text-[#889696] font-poppins text-base md:text-lg font-medium flex-1 text-center">
            Continue with Facebook
          </span>
        </button>
      </div>
    </AuthFormContainer>
  );
}
